<template>
  <v-card elevation="0">
    <v-card-title v-if="!!title">
      <span class="headline font-weight-light"> {{ title }}</span>
    </v-card-title>
    <v-divider v-if="!!title"></v-divider>
    <v-card-text>
      <v-form>
        <v-alert density="compact" type="error" v-if="errorMessage">{{
          errorMessage
        }}</v-alert>
        <v-text-field
          v-model="username"
          label="Username"
          :error-messages="usernameErrors"
          v-if="resetToken"
        ></v-text-field>
        <v-text-field
          v-model="oldPassword"
          label="Old Password"
          type="password"
          :error-messages="oldPasswordErrors"
          v-if="!resetToken"
        ></v-text-field>
        <v-text-field
          v-model="newPassword"
          label="New Password"
          type="password"
          :error-messages="newPasswordErrors"
          @focus="newPasswordFocused = true"
          @blur="newPasswordFocused = false"
        ></v-text-field>
        <div v-if="newPasswordFocused" class="mb-5">
          <AccountPasswordResetFormRequirementIndicator
            requirementText="Use at least 8 characters"
            :isValid="requirements.length"
          ></AccountPasswordResetFormRequirementIndicator>
          <AccountPasswordResetFormRequirementIndicator
            requirementText="Use upper and lower case letters (e.g Aa)"
            :isValid="requirements.usesUpperAndLowerCaseLetters"
          ></AccountPasswordResetFormRequirementIndicator>
          <AccountPasswordResetFormRequirementIndicator
            requirementText="Use a special character (e.g !@#$%)"
            :isValid="requirements.usesSpecialCharacter"
          ></AccountPasswordResetFormRequirementIndicator>
        </div>
        <v-text-field
          v-model="confirmPassword"
          label="Confirm New Password"
          type="password"
          :error-messages="confirmPasswordErrors"
          @focus="confirmPasswordFocused = true"
          class="mb-5"
        ></v-text-field>
        <div v-if="confirmPasswordFocused" class="mb-5">
          <AccountPasswordResetFormRequirementIndicator
            requirementText="Passwords match"
            :isValid="passwordsMatch"
          ></AccountPasswordResetFormRequirementIndicator>
        </div>
      </v-form>
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-btn @click="cancelClicked" variant="text">Cancel</v-btn>
      <v-btn color="accent" @click="resetPassword"
        >{{ buttonText }}
        <v-progress-circular
          v-if="isLoading"
          class="ml-2"
          :size="20"
          :width="2"
          indeterminate
        ></v-progress-circular
      ></v-btn>
    </v-card-actions>
  </v-card>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import AccountPasswordResetFormRequirementIndicator from "@/components/Account/AccountPasswordResetFormRequirementIndicator.vue";
import {
  PasswordValidateRegex,
  validatePasswordMeetsRequirements,
} from "@/helpers/Account/AccountPasswordResetHelpers";
import { NotificationType } from "@/enumerations/Notifications/NotificationType";
import type Notification from "@/interfaces/Notifications/Notification";
import type UnprocessableEntityReponse from "@/interfaces/Repository/UnprocessableEntityResponse";
import type WebLoginAuthentication from "@/interfaces/WebLogin/WebLoginAuthentication";
import {
  resetPassword as resetPasswordRepository,
  resetPasswordWithToken,
} from "@/repositories/Web/WebLoginRepository";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";

const props = withDefaults(
  defineProps<{
    title?: string;
    resetToken?: string | null;
  }>(),
  {
    title: "",
    resetToken: null,
  }
);

const emit = defineEmits<{
  onCancel: [];
  "account-password-reset-form-success": [];
}>();

const router = useRouter();
const authStore = useAuthStore();
const notificationsStore = useNotificationsStore();

const form = ref<{ reset: () => void } | null>(null);
const isLoading = ref(false);
const username = ref("");
const usernameErrors = ref<string[]>([]);
const oldPassword = ref("");
const oldPasswordErrors = ref<string[]>([]);
const newPassword = ref("");
const newPasswordErrors = ref<string[]>([]);
const newPasswordFocused = ref(false);
const confirmPassword = ref("");
const confirmPasswordErrors = ref<string[]>([]);
const confirmPasswordFocused = ref(false);
const errorMessage = ref("");

const requirements = computed(() =>
  validatePasswordMeetsRequirements(newPassword.value)
);

const buttonText = computed(() =>
  props.resetToken ? "Reset Password" : "Change Password"
);

const passwordsMatch = computed(
  () =>
    newPassword.value.length > 0 &&
    confirmPassword.value === newPassword.value
);

async function resetPassword(): Promise<void> {
  confirmPasswordFocused.value = false;
  errorMessage.value = "";

  if (!validate()) {
    return;
  }

  isLoading.value = true;

  try {
    if (props.resetToken) {
      await resetPasswordByToken();
    } else {
      await resetPasswordByOldPassword();
    }
  } finally {
    isLoading.value = false;
  }
}

async function resetPasswordByToken(): Promise<void> {
  if (!props.resetToken) {
    return;
  }

  const response = await resetPasswordWithToken(
    props.resetToken,
    username.value,
    newPassword.value
  );

  if (response.status === 422) {
    const errors = response.data as UnprocessableEntityReponse[];
    errorMessage.value =
      errors.find((error) => error.property === "")?.message ?? "";
    return;
  }

  const webAuthentication = response.data as WebLoginAuthentication;
  authStore.setWebTokens(webAuthentication.tokens);
  await router.push("/home");
}

async function resetPasswordByOldPassword(): Promise<void> {
  if (!authStore.tokens) {
    return;
  }

  const response = await resetPasswordRepository(
    authStore.tokens.jsonWebToken,
    oldPassword.value,
    newPassword.value
  );

  if (!response) {
    return;
  }

  if (response.invalidCredentials) {
    oldPasswordErrors.value = ["Invalid password"];
    return;
  }

  form.value?.reset();

  authStore.setWebTokens(response.tokens);
  notificationsStore.setNotification({
    message: "Password successfully reset",
    type: NotificationType.Success,
  } as Notification);
  emit("account-password-reset-form-success");
}

function validate(): boolean {
  let hasErrors = false;
  usernameErrors.value = [];
  oldPasswordErrors.value = [];
  newPasswordErrors.value = [];
  confirmPasswordErrors.value = [];

  if (props.resetToken && username.value.length === 0) {
    hasErrors = true;
    usernameErrors.value.push("Required");
  }

  if (!props.resetToken && oldPassword.value.length === 0) {
    hasErrors = true;
    oldPasswordErrors.value.push("Required");
  }

  if (oldPassword.value === newPassword.value) {
    hasErrors = true;
    oldPasswordErrors.value.push("New and old passwords cannot be the same");
  }

  if (!PasswordValidateRegex.test(newPassword.value)) {
    hasErrors = true;
    newPasswordErrors.value.push("Password does not meet all requirements");
  }

  if (!passwordsMatch.value) {
    hasErrors = true;
    confirmPasswordErrors.value.push("Passwords do not match");
  }

  return !hasErrors;
}

function cancelClicked(): void {
  emit("onCancel");
}
</script>
